/**
 * useHeaderState Hook
 *
 * State management for all Header system state variables with
 * persistence, validation, and utilities. This is the main hook that orchestrates
 * all header state management.
 */

import { useEffect, useCallback, useReducer } from 'react'
import { HEADER_CLICK_TYPES } from '../types/menuTypes'
import { headerStateReducer } from '../utils/headerStateReducer'
import {
  HEADER_STATE_ACTIONS,
  initialHeaderState
} from '../utils/headerStateConfig'
import {
  persistState,
  getPersistedState
} from '../utils/headerStatePersistence'
import { validateHeaderState } from '../utils/headerStateValidation'

/**
 * Main header state management hook
 * @param {boolean} persistToDisk - Whether to persist state to localStorage
 * @returns {Object} State management interface
 */
export const useHeaderState = (persistToDisk = true) => {
  // Initialize state with persisted data or defaults
  const [state, dispatch] = useReducer(
    headerStateReducer,
    persistToDisk ? getPersistedState() : initialHeaderState
  )

  // Persist state changes
  useEffect(() => {
    if (persistToDisk && validateHeaderState(state)) {
      persistState(state)
    }
  }, [state, persistToDisk])

  // Clear all timeouts on unmount
  useEffect(() => {
    return () => {
      dispatch({ type: HEADER_STATE_ACTIONS.CLEAR_ALL_TIMEOUTS })
    }
  }, [])

  // State management utilities
  const toggleMenu = useCallback(menuKey => {
    dispatch({
      type: HEADER_STATE_ACTIONS.TOGGLE_MENU,
      payload: { menuKey }
    })
  }, [])

  const setMenuVisibility = useCallback((menuKey, isVisible) => {
    dispatch({
      type: HEADER_STATE_ACTIONS.SET_MENU_VISIBILITY,
      payload: { menuKey, isVisible }
    })
  }, [])

  const setCurrentClick = useCallback(clickType => {
    dispatch({
      type: HEADER_STATE_ACTIONS.SET_CURRENT_CLICK,
      payload: { clickType }
    })
  }, [])

  const setActiveCategory = useCallback(categoryName => {
    dispatch({
      type: HEADER_STATE_ACTIONS.SET_ACTIVE_CATEGORY,
      payload: { categoryName }
    })
  }, [])

  const closeAllMenus = useCallback(() => {
    dispatch({ type: HEADER_STATE_ACTIONS.CLOSE_ALL_MENUS })
  }, [])

  const setSearchTerm = useCallback(searchTerm => {
    dispatch({
      type: HEADER_STATE_ACTIONS.SET_SEARCH_TERM,
      payload: { searchTerm }
    })
  }, [])

  const clearSearch = useCallback(() => {
    dispatch({ type: HEADER_STATE_ACTIONS.CLEAR_SEARCH })
  }, [])

  const setTimeoutId = useCallback((timeoutKey, timeoutId) => {
    dispatch({
      type: HEADER_STATE_ACTIONS.SET_TIMEOUT_ID,
      payload: { timeoutKey, timeoutId }
    })
  }, [])

  const clearTimeoutId = useCallback(timeoutKey => {
    dispatch({
      type: HEADER_STATE_ACTIONS.CLEAR_TIMEOUT_ID,
      payload: { timeoutKey }
    })
  }, [])

  const clearAllTimeouts = useCallback(() => {
    dispatch({ type: HEADER_STATE_ACTIONS.CLEAR_ALL_TIMEOUTS })
  }, [])

  const setNotification = useCallback((showNotification, welcomeMessage) => {
    dispatch({
      type: HEADER_STATE_ACTIONS.SET_NOTIFICATION,
      payload: { showNotification, welcomeMessage }
    })
  }, [])

  const clearNotification = useCallback(() => {
    dispatch({ type: HEADER_STATE_ACTIONS.CLEAR_NOTIFICATION })
  }, [])

  const batchUpdate = useCallback(updates => {
    dispatch({
      type: HEADER_STATE_ACTIONS.BATCH_UPDATE,
      payload: { updates }
    })
  }, [])

  const resetState = useCallback(() => {
    dispatch({ type: HEADER_STATE_ACTIONS.RESET_STATE })
  }, [])

  // Complex state operations
  const handleMenuClick = useCallback(
    clickType => {
      setCurrentClick(clickType)

      const menuUpdates = {}
      switch (clickType) {
        case HEADER_CLICK_TYPES.SELECTED_ACCOUNT:
          menuUpdates.isViewFilesMenuVisible = false
          menuUpdates.isMobileTopLevelMenuVisible = false
          menuUpdates.isCreateKinCategoryMenuVisible = false
          break
        case HEADER_CLICK_TYPES.SELECTED_VIEW_FILES:
          menuUpdates.isAccountMenuVisible = false
          menuUpdates.isCreateKinCategoryMenuVisible = false
          break
        case HEADER_CLICK_TYPES.SELECTED_VIEW_FILES_DROPDOWN:
          menuUpdates.isViewFilesMenuVisible = false
          menuUpdates.isCreateKinCategoryMenuVisible = false
          menuUpdates.isAccountMenuVisible = false
          break
        case HEADER_CLICK_TYPES.SELECTED_CREATE_KIN_CATEGORY:
          menuUpdates.isViewFilesMenuVisible = false
          menuUpdates.isAccountMenuVisible = false
          break
        case HEADER_CLICK_TYPES.SELECTED_CREATE_KIN_CATEGORY_DROPDOWN:
          menuUpdates.isViewFilesMenuVisible = false
          menuUpdates.isCreateKinCategoryMenuVisible = false
          menuUpdates.isAccountMenuVisible = false
          break
        case HEADER_CLICK_TYPES.SELECTED_MOBILE_TOP_LEVEL:
          menuUpdates.isAccountMenuVisible = false
          break
        case HEADER_CLICK_TYPES.SELECTED_SIGN_IN:
          menuUpdates.isSignInHamburgerMenuVisible = false
          break
        case HEADER_CLICK_TYPES.CLOSE_ALL:
          closeAllMenus()
          return
        default:
          console.warn('Unknown menu click type:', clickType)
          break
      }

      if (Object.keys(menuUpdates).length > 0) {
        batchUpdate({ menu: { ...state.menu, ...menuUpdates } })
      }
    },
    [state.menu, setCurrentClick, closeAllMenus, batchUpdate]
  )

  return {
    // State
    state,

    // Menu state accessors
    menu: state.menu,
    search: state.search,
    timeouts: state.timeouts,
    notifications: state.notifications,

    // Basic state management
    toggleMenu,
    setMenuVisibility,
    setCurrentClick,
    setActiveCategory,
    closeAllMenus,

    // Search management
    setSearchTerm,
    clearSearch,

    // Timeout management
    setTimeoutId,
    clearTimeoutId,
    clearAllTimeouts,

    // Notification management
    setNotification,
    clearNotification,

    // Complex operations
    handleMenuClick,
    batchUpdate,
    resetState,

    // Utilities
    validateHeaderState: () => validateHeaderState(state),
    dispatch
  }
}

// Export action types and config for external usage
export {
  HEADER_STATE_ACTIONS,
  initialHeaderState
} from '../utils/headerStateConfig'

export {
  persistState,
  getPersistedState
} from '../utils/headerStatePersistence'
export { validateHeaderState } from '../utils/headerStateValidation'
